/**
 * @file useStickyColumnOffsets.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-27
 * @modified 2026-08-27
 * @description Measured `left` offsets for a run of sticky table columns.
 *
 *              A sticky column has to be told how far in it sits, and the usual
 *              answer is to write the number down: column 3 starts at 254px
 *              because the two before it are 44 and 210. That holds only while
 *              every column before it has a fixed width. Give one of them
 *              `width: auto` — which is what you want for a name column that
 *              should take up the slack — and every number after it is a guess
 *              that happens to be wrong.
 *
 *              So measure instead. The hook walks the sticky cells in the header
 *              row, accumulates their widths, and writes each running total to
 *              `--sticky-left-N` on the table. The CSS stays declarative and
 *              stops carrying arithmetic it cannot actually do.
 *
 *              It counts the cells that are really there, which also settles the
 *              case where the number of sticky columns changes — the blend grid
 *              grows a second quantity column when comparing, and hard-coded
 *              offsets stacked it underneath the first.
 */

import { useLayoutEffect, useCallback, useRef } from 'react';

const STICKY = /(^|\s)sticky-col-\d+(\s|$)/;

export default function useStickyColumnOffsets(tableRef, deps = []) {
  // Remembered so a measurement that changed nothing doesn't touch the DOM —
  // this runs from a ResizeObserver, and writing a custom property that feeds
  // layout is a good way to make the observer fire again.
  const last = useRef('');

  const measure = useCallback(() => {
    const table = tableRef.current;
    const head = table?.tHead?.rows?.[0];
    if (!head) return;

    const cells = Array.from(head.cells).filter((c) => STICKY.test(c.className));
    if (!cells.length) return;

    // Offset N is the width of everything before column N, so the first is
    // always 0 and the last total is the width of the whole frozen block.
    const offsets = [];
    let running = 0;
    for (const cell of cells) {
      offsets.push(running);
      running += cell.getBoundingClientRect().width;
    }

    const signature = offsets.join(',');
    if (signature === last.current) return;
    last.current = signature;

    offsets.forEach((left, i) => {
      table.style.setProperty(`--sticky-left-${i + 1}`, `${left}px`);
    });
    // The width of the frozen block, for anything that needs to clear it.
    table.style.setProperty('--sticky-left-end', `${running}px`);
  }, [tableRef]);

  useLayoutEffect(() => {
    measure();

    const table = tableRef.current;
    if (!table || typeof ResizeObserver === 'undefined') return undefined;

    // Observe the header cells themselves, not just the table: an auto-width
    // column can change width from its own content while the table does not
    // change size at all.
    const ro = new ResizeObserver(measure);
    ro.observe(table);
    const head = table.tHead?.rows?.[0];
    if (head) Array.from(head.cells).forEach((c) => ro.observe(c));

    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, ...deps]);
}
